/**
 * The party panel's keyrace row, computed once for both scenes
 * (keyracing.md D2, decision 11-15).
 *
 * The town and a floor show the SAME row from the SAME `RaceState` packet, and
 * the two scenes cannot share a matcher: in town I am a hub member and the wire's
 * member ids are hub ids, while a side that has opened its door has no town
 * presences at all (its members arrive with `id: -1`). So the caller supplies
 * `isMe` — the hub matches my hub id, the floor matches my name, which the roster
 * carries from Hello and which no two accounts share.
 *
 * What lives here is projection only. The client judges nothing about a race: it
 * cannot arm one, forfeit one, or say anything about the other side's dungeon.
 */
import type { RaceMemberWire, RaceWire } from "@pd/shared";
import type { RaceRowInfo } from "./hud.js";

/** `ms` in `m:ss`. */
export function mmss(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * A local countdown anchored to the moment the packet arrived.
 *
 * The wire carries REMAINING durations and never a timestamp — the client's clock
 * is not the server's — and `RaceState` is rebroadcast only when a race CHANGES,
 * so a clock that waited for packets would sit frozen at 29:01 while the server's
 * window ran out. This is presentation of the same kind the floor does for
 * movement: it renders a clock, it never decides one. The forfeit stays entirely
 * the server's.
 */
export function raceRemaining(ms: number, seenAt: number): number {
  if (ms <= 0) return 0;
  const now = typeof performance !== "undefined" ? performance.now() : 0;
  return Math.max(0, ms - (now - seenAt));
}

/** The side of `race` I am on, or null. */
export function raceMySide(
  race: RaceWire,
  isMe: (m: RaceMemberWire) => boolean,
): 0 | 1 | null {
  for (const i of [0, 1] as const) {
    if (race.sides[i].members.some(isMe)) return i;
  }
  return null;
}

/**
 * The side a race still WAITS ON ME for, or null. The one predicate the row, the
 * door and the clocks must agree on.
 *
 * "Armed" is a fact about the RACE; this is the fact about MY side of it. A side
 * whose `outcome` has landed is done — it may not open that door again, it may
 * take an ordinary floor, it may race again tonight — while the race itself stays
 * armed because the OTHER side's start window is still running. `startsInMs` is
 * that window, not mine. Reading `state !== "open"` as "still in it" made a party
 * that walked out of its race floor read as one locked out for the remaining half
 * hour, which is the opposite of what the server had just done (released on the
 * floor-ended tick, party disbanded, ordinary door open).
 */
export function raceWaitsOnMe(
  race: RaceWire,
  isMe: (m: RaceMemberWire) => boolean,
): 0 | 1 | null {
  if (race.state === "open") return null;
  const side = raceMySide(race, isMe);
  return side !== null && race.sides[side].outcome === null ? side : null;
}

/** Whether a race naming me still has a clock worth ticking once a second. */
export function raceHasClock(
  race: RaceWire,
  isMe: (m: RaceMemberWire) => boolean,
): boolean {
  const side = raceMySide(race, isMe);
  if (side === null) return false;
  return race.state === "open"
    ? race.expiresInMs > 0
    : race.sides[side].outcome === null && race.startsInMs > 0;
}

/**
 * The row's content for the freshest race state, or null for no row.
 *
 * A LIVE commitment (armed or racing, and MY side not yet settled) outranks an
 * open offer: the offer keeps its own window and its chat line, and the row is not
 * a queue. With nothing live, an open challenge shows its count and the verb that
 * reopens the window — dismissing a challenge is NOT answering it (silence is not
 * consent; expiry is the issuer's out), so the row is where it stays visible.
 *
 * A side that has its outcome is not a commitment and gets no row: the forfeit
 * already said so in chat, and the verdict is D1's surface, not this one. The
 * race may still be armed for the other side — that is their clock.
 */
export function raceRowInfo(opts: {
  races: RaceWire[];
  seenAt: number;
  isMe: (m: RaceMemberWire) => boolean;
  /** A side's display name — the hub resolves its host's name, a floor reads the
   *  roster the packet carries (its own hub ids are gone). */
  sideName: (race: RaceWire, side: 0 | 1) => string;
  /** Whether the host of a side is me: only that player can open the door. */
  hostIsMe: (hostId: number) => boolean;
  /** Reopen the consent window (the row's Review verb). Absent = no verb, which is
   *  what a floor passes: the window is the town's surface. */
  onReview?: () => void;
}): RaceRowInfo | null {
  const mine = opts.races.filter((r) => raceMySide(r, opts.isMe) !== null);
  const live = mine.find((r) => raceWaitsOnMe(r, opts.isMe) !== null);
  if (live) {
    const side = raceWaitsOnMe(live, opts.isMe)!;
    const other = (1 - side) as 0 | 1;
    const left = raceRemaining(live.startsInMs, opts.seenAt);
    const running = live.state === "racing";
    return {
      clock: running || left <= 0
        ? { text: "both in", tone: "done" }
        : { text: `${mmss(left)} to start`, tone: left < 60_000 ? "low" : "normal" },
      mine: { name: opts.sideName(live, side), inIt: live.sides[side].started },
      theirs: { name: opts.sideName(live, other), inIt: live.sides[other].started },
      iAmHost: opts.hostIsMe(live.sides[side].host),
    };
  }
  const open = mine.filter((r) => r.state === "open");
  if (open.length === 0) return null;
  const race = open[0]!;
  const side = raceMySide(race, opts.isMe)!;
  const other = (1 - side) as 0 | 1;
  const yes = race.sides[side].members.filter((m) => m.accepted).length;
  const left = raceRemaining(race.expiresInMs, opts.seenAt);
  return {
    clock: { text: `${mmss(left)} to answer`, tone: left < 60_000 ? "low" : "normal" },
    mine: { name: opts.sideName(race, side), inIt: false },
    theirs: { name: opts.sideName(race, other), inIt: false },
    iAmHost: false,
    challenge: {
      text: open.length > 1
        ? `${open.length} challenges waiting · ${yes} of ${race.sides[side].members.length} accepted`
        : `Challenge from ${opts.sideName(race, other)} · ${yes} of ${race.sides[side].members.length} accepted`,
      onReview: () => opts.onReview?.(),
    },
  };
}
